import { bindActionCreators } from '@reduxjs/toolkit';
import Slider from 'rc-slider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type { WaykeLocation, WaykeQueryLocation } from '../../@types/WaykeLocation';
import { clear, current, lookup, search } from '../../actions/geo';
import type QueryFilter from '../../helpers/query-filter';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import type { CombinedFacet } from '../../utils/reselectors';
import { SpinnerInline } from '../spinner';
import FilterWxPanel from './filter-wx-panel';
import { QueryFoldoutBlock, QueryFoldoutItem } from './query-foldout';
import SelectFilterLocationMap from './select-filter-location-map';

interface SelectFilterLocationProps {
  queryFilter: QueryFilter;
  radius?: number;
  facet: CombinedFacet;
  onCloseFoldout: () => void;
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const SelectFilterLocation = ({
  facet,
  queryFilter,
  onCloseFoldout,
  onRequestSearch,
}: SelectFilterLocationProps) => {
  const { t } = useTranslation(['common', 'search']);
  const dispatch = useDispatch();

  const requestStatus = useSelector<RootState, number>((s) => s.geo.requestStatus);
  const responseStatus = useSelector<RootState, number>((s) => s.geo.responseStatus);
  const position = useSelector<RootState, WaykeLocation | null>((s) => s.geo.location);
  const results = useSelector<RootState, WaykeLocation[]>((s) => s.geo.results || []);

  const actions = bindActionCreators({ search, clear, current }, dispatch);

  const requestGet = useDataExchangeStatus(ExchangeTypes.Fetching, [requestStatus, responseStatus]);
  const timeoutId = useRef<NodeJS.Timeout>(undefined);

  const initialLocation = queryFilter.hasSpatial()
    ? {
        ...(queryFilter.getSpatial() as WaykeQueryLocation),
        radius: (queryFilter.getSpatial() as WaykeQueryLocation)?.distance,
      }
    : position
      ? position
      : undefined;

  const [searchTerm, setSearchTerm] = useState('');
  const [currentLocation, setCurrentLocation] = useState(initialLocation);
  const [open, setOpen] = useState(false);
  const currentLocationRef = useRef(currentLocation);
  currentLocationRef.current = currentLocation;

  useEffect(() => {
    if (!position) {
      actions.current();
    }
  }, []);

  useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      if (searchTerm) {
        actions.search(searchTerm);
      } else {
        actions.clear();
      }
    }, 500);
  }, [searchTerm]);

  useEffect(() => {
    if (!currentLocation && position) {
      setCurrentLocation({
        ...position,
      });
    }
  }, [position?.lat, position?.lng]);

  const onRequestCurrentPosition = () => {
    if (!position) {
      actions.current();
    } else {
      setCurrentLocation({
        ...position,
      });
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && results.length > 0) {
      onSelect(results[0]);
    }
  };

  const onSliderChange = (radius: number | number[]) => {
    if (currentLocation) {
      setCurrentLocation({
        ...currentLocation,
        radius: radius as number,
      });
    }
  };

  const onSelect = (location: WaykeLocation) => {
    setCurrentLocation(location);
    actions.clear();
  };

  const onPositionChange = (lat: number, lng: number) => {
    lookup(lat, lng, (json) => {
      let match = json.results.find((r) => r.types?.includes('administrative_area_level_2'));

      if (!match) {
        match = json.results.find((r) => r.types?.includes('political'));
      }

      if (match && currentLocationRef.current) {
        const name = match.address_components?.[0].long_name || match.formatted_address || '';
        const nextLocation = {
          ...currentLocationRef.current,
          lat,
          lng,
          city: name,
          name,
          radius: currentLocationRef.current?.radius || 50,
        };
        setCurrentLocation(nextLocation);
      }
    });
  };

  const onOpen = () => setOpen(true);

  const onRequestSearchClick = () => {
    if (currentLocation?.lat && currentLocation.lng) {
      const nextActive = currentLocation as WaykeLocation;
      const nextQueryFilter = queryFilter.removePagination().addSpatial(nextActive);
      onRequestSearch(nextQueryFilter);
      onCloseFoldout();
    }
  };

  let resultsMarkup = null;
  if (open || !currentLocation) {
    if (requestGet.pending) {
      resultsMarkup = (
        <QueryFoldoutBlock title={t('Search results', { ns: 'search' })} visible={true}>
          <QueryFoldoutItem active={false} onClick={() => false}>
            <SpinnerInline />
          </QueryFoldoutItem>
        </QueryFoldoutBlock>
      );
    } else if (results.length > 0) {
      resultsMarkup = (
        <QueryFoldoutBlock title={t('Search results', { ns: 'search' })} visible={true}>
          {results.map((result) => (
            <QueryFoldoutItem
              key={`${result.lat}${result.lng}`}
              active={false}
              onClick={() => onSelect(result)}
            >
              <div data-am-columnrow="always">
                <div className="column">
                  {result.city} <span className="text-dark-lighten">+50 km</span>
                </div>
                <div className="column text-dark-lighten">
                  <div className="text-primary font-medium font-size-small">
                    {t('Choose', { ns: 'common' })}
                  </div>
                </div>
              </div>
            </QueryFoldoutItem>
          ))}
        </QueryFoldoutBlock>
      );
    }
  }

  const buttonOrInput = (
    <div className="filter-map-tools-search">
      <div data-wx-inputsearch="thin" className={open && resultsMarkup ? 'foldout-open' : ''}>
        <div className="input-search-input-section">
          <form
            action=""
            className="input-search-input-form"
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
          >
            <input
              type="search"
              className="input-search-input"
              aria-label={t('Search', { ns: 'common' })}
              placeholder={t('Search place', { ns: 'search' })}
              onFocus={onOpen}
              onChange={onChange}
              onKeyDown={onKeyDown}
              value={searchTerm}
            />
            <button type="button" className="input-search-clear" style={{ display: 'none' }} />
          </form>
        </div>
        {resultsMarkup && (
          <div className="input-search-foldout">
            <div className="input-search-foldout-main">{resultsMarkup}</div>
          </div>
        )}
        <div className="input-search-action-section">
          <button
            type="button"
            className="input-search-action is-action"
            title={t('Search', { ns: 'common' })}
          >
            <i className="icon-search no-margin" />
          </button>
        </div>
      </div>
    </div>
  );

  if (!currentLocation) {
    return (
      <FilterWxPanel modifiers="map" title={facet.displayName} onRequestSearch={onRequestSearch}>
        <div className="filter-panel-body-content">
          <div data-wx-filtermap="">
            <div className="filter-map-container is-placeholder">
              <div className="filter-map" />
              <div className="filter-map-tools">{buttonOrInput}</div>
            </div>
          </div>
        </div>
      </FilterWxPanel>
    );
  }

  return (
    <FilterWxPanel modifiers="map" title={facet.displayName} onRequestSearch={onRequestSearch}>
      <div className="filter-panel-body-content">
        <div data-wx-filtermap="">
          <SelectFilterLocationMap
            position={
              currentLocation
                ? {
                    lat: currentLocation.lat,
                    lng: currentLocation.lng,
                  }
                : undefined
            }
            onRequestCurrentPosition={onRequestCurrentPosition}
            radius={currentLocation?.radius}
            onChange={onPositionChange}
          >
            {buttonOrInput}
          </SelectFilterLocationMap>
          <div className="filter-map-range">
            <div className="filter-map-range-slider">
              <div data-am-rangeslider="simple">
                <div className="range-slider">
                  <Slider
                    min={10}
                    max={750}
                    step={10}
                    value={currentLocation.radius}
                    disabled={!currentLocation}
                    onChange={onSliderChange}
                  />
                </div>
              </div>
            </div>
            <div className="filter-map-range-value">+{currentLocation.radius} km</div>
          </div>
        </div>
      </div>
      <div className="filter-panel-body-footer">
        <div className="filter-panel-body-footer-action-container">
          <div className="filter-panel-body-footer-action">
            <button
              type="button"
              data-wx-button="action narrow full-width"
              title={t('Confirm location', { ns: 'search' })}
              disabled={!currentLocation}
              onClick={onRequestSearchClick}
            >
              <div className="button-icon">
                <i className="icon-check no-margin" />
              </div>
              <div className="button-text">{t('Confirm location', { ns: 'search' })}</div>
            </button>
          </div>
        </div>
      </div>
    </FilterWxPanel>
  );
};

export default SelectFilterLocation;
